import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { STORIES } from "@/data/content";
import { Quote, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CANDLES =
  "https://images.pexels.com/photos/5718961/pexels-photo-5718961.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Stories = () => {
  const [community, setCommunity] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/stories/approved`)
      .then((res) => setCommunity(res.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="stories-page">
      <section className="relative" data-testid="stories-hero">
        <div className="absolute inset-0">
          <img src={CANDLES} alt="Candles in remembrance" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/40 via-[#0A0A0C]/70 to-[#0A0A0C]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-32 lg:py-40">
          <div className="overline text-[#D4AF37]">Voices from Sinjar</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EBE5] mt-4 max-w-3xl leading-tight">
            Survivors are the truest record.
          </h1>
          <p className="text-[#A19D98] mt-6 max-w-2xl leading-relaxed">
            What follows are excerpts shaped from publicly available testimonies.
            They are shared here with the deepest respect for the courage it
            takes to speak.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 space-y-20" data-testid="stories-curated">
        <div className="overline">Public testimonies</div>
        {STORIES.map((s, i) => (
          <article
            key={i}
            className="border-l border-[#D4AF37]/40 pl-8 lg:pl-12"
            data-testid={`story-${i}`}
          >
            <Quote className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.25} />
            <blockquote className="font-serif-display text-xl lg:text-2xl italic text-[#F2EBE5] leading-snug">
              {s.excerpt}
            </blockquote>
            <div className="mt-8">
              <div className="text-[#F2EBE5] text-base">{s.name}</div>
              <div className="overline mt-2">
                {s.age} · {s.village}
              </div>
              <div className="text-xs text-[#6E6B67] mt-3 italic">{s.note}</div>
            </div>
          </article>
        ))}
      </section>

      {community.length > 0 && (
        <section
          className="max-w-4xl mx-auto px-6 lg:px-12 pb-16 space-y-16 border-t border-white/10 pt-24"
          data-testid="stories-community"
        >
          <div>
            <div className="overline text-[#D4AF37]">From the community</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#F2EBE5] mt-3">
              Voices shared with us.
            </h2>
            <p className="text-sm text-[#A19D98] mt-4 max-w-2xl leading-relaxed">
              Each of the following was submitted by a reader and personally
              reviewed by Ghazy Halou before being published.
            </p>
          </div>
          {community.map((s) => (
            <article
              key={s.id}
              className="border-l border-[#D4AF37]/40 pl-8 lg:pl-12"
              data-testid={`community-story-${s.id}`}
            >
              <Quote className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.25} />
              <blockquote className="font-serif-display text-lg lg:text-xl italic text-[#F2EBE5] leading-relaxed whitespace-pre-wrap">
                {s.story}
              </blockquote>
              <div className="mt-8">
                <div className="text-[#F2EBE5] text-base">{s.name}</div>
                {(s.relationship || s.location) && (
                  <div className="overline mt-2">
                    {[s.relationship, s.location].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </article>
          ))}
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-24 border-t border-white/10" data-testid="stories-share-cta">
        <div className="overline text-[#D4AF37]">Your voice matters</div>
        <h2 className="font-serif-display text-3xl lg:text-4xl text-[#F2EBE5] mt-3 max-w-2xl">
          Have a story to share?
        </h2>
        <p className="text-[#A19D98] mt-4 max-w-2xl leading-relaxed">
          If you are a survivor, a family member, or a witness — your account
          belongs here. Stories are sent privately to Ghazy Halou and are only
          published with your consent.
        </p>
        <Link
          to="/share"
          data-testid="stories-share-link"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500"
        >
          Share your story <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </section>
    </div>
  );
};

export default Stories;
